import { Component, Input, SimpleChanges } from '@angular/core';
import {
    ApproversMissionApprovalDto,
    DisplayableMissionDto,
    findActiveApprovals,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionMessageDto,
    MissionService,
    PersonDto,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    ApprovalScreenStatus,
    findActiveMissionApprovals,
    getSimplifiedAuthorityFromApproval
} from '../helpers';
import { MissionApprovalWithAuthority } from '../interfaces';

@Component({
    selector: 'summary-header',
    templateUrl: './summary-header.component.html',
    styleUrls: ['./summary-header.component.scss']
})
export class SummaryHeader {
    @Input() mission: DisplayableMissionDto;
    @Input() organisation: PersonsOrganisationDto;
    @Input() approval?: ApproversMissionApprovalDto;
    @Input() missionApproval?: MissionApprovalWithAuthority;
    @Input() missionApprovals: MissionApprovalWithAuthority[] = [];
    @Input() status: ApprovalScreenStatus;
    @Input() working: boolean;

    activeApprovals: MissionApprovalWithAuthority[] = [];
    getSimplifiedAuthorityFromApproval = getSimplifiedAuthorityFromApproval;

    rpic: PersonDto;
    discussions: MissionMessageDto[];
    message: string;
    toggle: any;

    /**
     * Messages can be read on every mission, but can't be added once it's
     * finalised, or by people who can't edit or approve it.
     */
    canSendMessage = false;
    readOnlyMessage: string = null;
    messagePlaceholder: string = null;

    loadingChat = false;
    summaryStatus: string;
    statusClass: string;

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private missionService: MissionService,
        private logging: FlyFreelyLoggingService
    ) {
        this.setAllToggleFalse();
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.loadingChat = working));
    }

    ngOnInit() {
        this.missionService.commsChanges$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshDiscussions());
        this.activeApprovals = findActiveMissionApprovals(
            this.missionApprovals
        );
        if (this.approval == null && this.activeApprovals?.length === 1) {
            this.approval = this.activeApprovals[0]
                .approval as ApproversMissionApprovalDto;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        this.activeApprovals = findActiveMissionApprovals(
            this.missionApprovals
        );
        if (this.approval == null && this.activeApprovals?.length === 1) {
            this.approval = this.activeApprovals[0]
                .approval as ApproversMissionApprovalDto;
        }

        if ('mission' in changes) {
            if (this.mission == null) {
                this.canSendMessage = false;
                this.readOnlyMessage = null;
                this.summaryStatus = null;
                this.statusClass = null;
                this.rpic = null;
                return;
            }
            // this.summaryStatus = this.getSummaryStatus();
            // this.statusClass = this.getStatusClass();

            const rpicRole = this.mission.missionCrewDetails.find(
                c => c.role.coreRole === 'PILOT_IN_COMMAND'
            );
            this.rpic = rpicRole != null ? rpicRole.person : null;
        }
        this.refreshCanSendMessage();
        this.refreshDiscussions();
    }

    /**
     * Uses the edit and approve permissions rather than `canEdit`, as messages
     * can be sent in every mission status except finalised.
     */
    private refreshCanSendMessage() {
        if (this.mission == null) {
            return;
        }
        const canEditOrApprove =
            this.mission.availableActions?.hasEdit === true ||
            this.approval?.availableActions?.hasApprove === true ||
            findActiveApprovals(this.mission.approvals).some(
                approval => approval.availableActions?.hasApprove === true
            );
        if (this.mission.status === DisplayableMissionDto.Status.FINALISED) {
            this.canSendMessage = false;
            this.readOnlyMessage =
                'This mission is finalised, so no new messages can be added.';
        } else if (!canEditOrApprove) {
            this.canSendMessage = false;
            this.readOnlyMessage =
                'Only people who can edit or approve this mission can add messages.';
        } else {
            this.canSendMessage = true;
            this.readOnlyMessage = null;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private setAllToggleFalse() {
        this.toggle = {
            isCriteria: false,
            isCondition: false,
            isActivity: false,
            isDocument: false
        };
    }

    private getApprovalStatus() {
        const approvalStatuses = findActiveApprovals(
            this.mission.approvals
        ).map(a => a.status);
        if (
            approvalStatuses.filter(status => status === 'BEING_REVIEWED')
                .length === approvalStatuses.length
        ) {
            return 'LOCKED';
        } else if (approvalStatuses.some(status => status === 'PENDING')) {
            return 'WAITING';
        }
        return findActiveApprovals(this.mission.approvals).map(a => a.status);
    }

    private getMissionStatus() {
        switch (this.mission.status) {
            case 'FLYING':
                return 'LOCKED';
            case 'READY_TO_FLY':
                return 'READY TO FLY';
            default:
                return this.mission.status;
        }
    }

    //     getStatusClass() {
    //         return `mission-status-${this.getSummaryStatus().toLowerCase()}`;
    //     }
    //
    //     getSummaryStatus() {
    //         if (this.approval != null) {
    //             return this.getApprovalStatus();
    //         } else {
    //             return this.getMissionStatus();
    //         }
    //     }

    isShowPanel(panel: string) {
        switch (panel) {
            case 'criteria':
                return this.approval != null;
            case 'conditions':
                return this.approval != null;
            case 'activity':
                return true;
            case 'documents':
                return true;
        }
    }

    getName(discussion: MissionMessageDto) {
        return `${discussion.sender.firstName} ${discussion.sender.lastName}`;
    }

    isRole(role: string) {
        return this.organisation.roles.find(r => r === role) ? true : false;
    }

    onTogglePress(toggle: string) {
        this.toggle = {
            isCriteria: false,
            isCondition: false,
            isActivity: false,
            isDocument: false,
            [toggle]: !this.toggle[toggle]
        };
    }

    refreshDiscussions() {
        if (this.mission == null) {
            return;
        }
        if (this.approval != null) {
            this.messagePlaceholder = 'Message the remote pilot';
        } else if (findActiveApprovals(this.mission.approvals).length > 0) {
            this.messagePlaceholder = 'Message the approver';
        } else {
            this.messagePlaceholder = 'Write a message';
        }
        // FIXME: Verify that mission.organisationId is the correct managing organisation ID
        const managingOrganisationId =
            this.mission.organisationId ?? this.organisation?.id;
        if (managingOrganisationId == null) {
            this.logging.error(
                null,
                'Cannot load discussions: missing managing organisation ID'
            );
            return;
        }
        this.missionService
            .findDiscussions(this.mission.id, managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: discussions => {
                    const reversedDiscussion = [...discussions];
                    reversedDiscussion.reverse();
                    this.discussions = reversedDiscussion;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading messages: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    sendMessage() {
        if (!this.canSendMessage) {
            return;
        }
        this.missionService
            .createDiscussion(this.mission.id, this.message)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.message = '';
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error sending message: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
